import React from "react";

import EastRoundedIcon from "@mui/icons-material/EastRounded";
import { Box, Typography } from "@mui/material";

import { FormulaToken } from "../utils/formulaTokens";

interface FormulaTokenRowProps {
    tokens: FormulaToken[];
}

const CHIP_STYLES: Record<
    FormulaToken["kind"],
    { backgroundColor: string; color: string; borderColor: string }
> = {
    function: {
        backgroundColor: "rgba(17, 119, 242, 0.08)",
        color: "#1177F2",
        borderColor: "rgba(17, 119, 242, 0.16)",
    },
    variable: {
        backgroundColor: "#f2f2f5",
        color: "#3A3A3A",
        borderColor: "#e5e5ea",
    },
    text: {
        backgroundColor: "transparent",
        color: "#6B6B6B",
        borderColor: "transparent",
    },
};

const FormulaTokenRow: React.FC<FormulaTokenRowProps> = ({ tokens }) => {
    if (!tokens.length) {
        return (
            <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: "13px" }}
            >
                —
            </Typography>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.75,
                mt: 1,
            }}
        >
            {tokens.map((token, index) => {
                const styles = CHIP_STYLES[token.kind];
                const showArrow =
                    index < tokens.length - 1 &&
                    token.kind !== "text" &&
                    tokens[index + 1]?.kind !== "text";

                return (
                    <React.Fragment key={`${token.kind}-${token.label}-${index}`}>
                        {token.kind === "text" ? (
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    color: styles.color,
                                    px: 0.25,
                                }}
                            >
                                {token.label}
                            </Typography>
                        ) : (
                            <Box
                                component="span"
                                title={token.label}
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    maxWidth: "100%",
                                    px: 1,
                                    py: 0.3,
                                    borderRadius: "8px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    lineHeight: 1.4,
                                    letterSpacing: "0.01em",
                                    backgroundColor: styles.backgroundColor,
                                    color: styles.color,
                                    border: `1px solid ${styles.borderColor}`,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {token.label}
                            </Box>
                        )}
                        {showArrow && (
                            <EastRoundedIcon
                                sx={{
                                    fontSize: 14,
                                    color: "rgba(0, 0, 0, 0.28)",
                                    flexShrink: 0,
                                }}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </Box>
    );
};

export default FormulaTokenRow;
